<script lang="ts" setup>
import { onMounted, ref, computed } from "vue";
import { useRouter } from "vue-router";
import PanelGrid from "@/views/shared/components/PanelGrid.vue";
import PanelCard from "@/views/shared/components/PanelCard.vue";
import ErrorState from "@/views/shared/components/ErrorState.vue";
import CreateOrgModal from "./components/CreateOrgModal.vue";
import { fetchWrapper } from "@/helpers/fetch-wrapper";
import PageHeader from "@/components/PageHeader.vue";
import DataView from "@/components/DataView.vue";
import type { Config } from "datatables.net";

const router = useRouter();

const baseUrl = `${import.meta.env.VITE_API_URL}/orgs`;
const showCreateModal = ref(false);

interface OrgFromApi {
  orgId: string;
  orgName: string;
  canPublishProducts: boolean;
  canPartnerWithOrgs: boolean;
  userCount: number;
  projectCount: number;
  createdUtc: string | null;
  lastActivityUtc: string | null;
}

type AdminOrg = OrgFromApi;

const organisations = ref<AdminOrg[]>([]);
const isLoading = ref(false);
const hasError = ref(false);
const errorType = ref<'connection' | 'http' | 'general'>('general');
const searchQuery = ref("");

async function loadOrganisations() {
  isLoading.value = true;
  hasError.value = false;
  try {
    // Take=100 is the server-side cap (see SearchRequest); the admin list view
    // loads the whole set and filters client-side.
    organisations.value = (await fetchWrapper.get(`${baseUrl}?take=100`)) as OrgFromApi[];
  } catch (error) {
    console.error("Error loading organisations:", error);
    hasError.value = true;
    errorType.value = 'connection';
  } finally {
    isLoading.value = false;
  }
}

const filteredOrganisations = computed(() => {
  if (!searchQuery.value) return organisations.value;
  const q = searchQuery.value.toLowerCase();
  return organisations.value.filter((org) => org.orgName.toLowerCase().includes(q));
});

const stats = computed(() => ({
  total: organisations.value.length,
  totalUsers: organisations.value.reduce((sum, o) => sum + o.userCount, 0),
  totalProjects: organisations.value.reduce((sum, o) => sum + o.projectCount, 0),
}));

function manageOrganisation(orgId: string) {
  // Navigate to organisation management page
  router.push(`/admin/organisations/${orgId}`);
}

function formatDate(iso: string | null): string {
  return iso ? new Date(iso).toLocaleDateString() : "—";
}

function formatTimeAgo(iso: string | null): string {
  if (!iso) return "—";
  const diffMs = Date.now() - new Date(iso).getTime();
  const diffMin = Math.floor(diffMs / 60_000);
  if (diffMin < 1) return "just now";
  if (diffMin < 60) return `${diffMin}m ago`;
  const diffHours = Math.floor(diffMin / 60);
  if (diffHours < 24) return `${diffHours}h ago`;
  const diffDays = Math.floor(diffHours / 24);
  if (diffDays === 1) return "1 day ago";
  return `${diffDays} days ago`;
}

function createOrganisation() {
  showCreateModal.value = true;
}

async function handleOrganisationCreated() {
  // Reload organisations list after successful creation
  await loadOrganisations();
}

const tableOptions: Config = {
  columns: [
    {
      data: 'orgName',
      title: 'Organisation',
      render: (d: string, _: string, row: AdminOrg) =>
        `<div><strong>${d}</strong><br><small class="text-muted">Created ${formatDate(row.createdUtc)}</small></div>`,
    },
    { data: 'userCount', title: 'Users', render: (d: number) => `<span class="fw-bold">${d}</span>` },
    { data: 'projectCount', title: 'Projects', render: (d: number) => `<span class="fw-bold">${d}</span>` },
    {
      data: 'canPublishProducts',
      title: 'Can Publish',
      render: (d: boolean) => d
        ? '<i class="bi bi-check-circle-fill text-success fs-5"></i>'
        : '<i class="bi bi-x-circle text-muted fs-5"></i>',
    },
    {
      data: 'canPartnerWithOrgs',
      title: 'Can Partner',
      render: (d: boolean) => d
        ? '<i class="bi bi-check-circle-fill text-success fs-5"></i>'
        : '<i class="bi bi-x-circle text-muted fs-5"></i>',
    },
    {
      data: 'lastActivityUtc',
      title: 'Last Activity',
      render: (d: string | null) => `<small class="text-muted">${formatTimeAgo(d)}</small>`,
    },
  ],
  layout: {
    topEnd: null,
    bottomEnd: null,
    bottomStart: null,
  },
};

onMounted(async () => {
  await loadOrganisations();
});
</script>

<template>
  <PanelGrid :columns="1">
    <PanelCard>
      <PageHeader
        title="All Organisations"
        :breadcrumbs="[
          { label: 'Dashboard', href: '/' },
          { label: 'Organisation Admin' }
        ]"
      >
        <template #actions>
          <button class="bb-btn-primary-cta" @click="createOrganisation">
            <i class="bi bi-plus-circle me-2"></i>Create Organisation
          </button>
        </template>
      </PageHeader>

      <!-- Summary Stats -->
      <div class="row g-3 mb-4">
        <div class="col-4">
          <div class="card text-center">
            <div class="card-body">
              <i class="bi bi-buildings fs-2 text-primary"></i>
              <div class="fs-4 fw-bold mt-2">{{ stats.total }}</div>
              <div class="text-muted small">Total Orgs</div>
            </div>
          </div>
        </div>
        <div class="col-4">
          <div class="card text-center">
            <div class="card-body">
              <i class="bi bi-people fs-2 text-info"></i>
              <div class="fs-4 fw-bold mt-2">{{ stats.totalUsers }}</div>
              <div class="text-muted small">Total Users</div>
            </div>
          </div>
        </div>
        <div class="col-4">
          <div class="card text-center">
            <div class="card-body">
              <i class="bi bi-boxes fs-2 text-warning"></i>
              <div class="fs-4 fw-bold mt-2">{{ stats.totalProjects }}</div>
              <div class="text-muted small">Total Projects</div>
            </div>
          </div>
        </div>
      </div>

      <!-- Filters -->
      <div class="mb-4">
        <input
          v-model="searchQuery"
          type="text"
          class="form-control"
          placeholder="Search organisations..."
        />
      </div>

      <!-- Loading State -->
      <div v-if="isLoading" class="text-center py-5">
        <div class="spinner-border text-primary" role="status">
          <span class="visually-hidden">Loading...</span>
        </div>
      </div>

      <!-- Error State -->
      <ErrorState
        v-else-if="hasError"
        :type="errorType"
        @retry="loadOrganisations"
      />

      <!-- Organisations List -->
      <DataView
        v-else
        :data="filteredOrganisations"
        :view-mode="'table'"
        :options="tableOptions"
        @row-click="(item) => manageOrganisation(item.orgId)"
      >
        <template #empty-state>
          <div class="text-center py-5 text-muted">
            <i class="bi bi-buildings fs-1 mb-3 d-block"></i>
            <p>No organisations found</p>
          </div>
        </template>
      </DataView>
    </PanelCard>
  </PanelGrid>

  <!-- Create Organisation Modal -->
  <CreateOrgModal
    v-model:show="showCreateModal"
    @created="handleOrganisationCreated"
  />
</template>

<style scoped lang="scss">
@import 'bootstrap/scss/functions';
@import 'bootstrap/scss/variables';
@import 'bootstrap/scss/mixins';

// On mobile DataView reshapes each row into a card (tbody.dt-grid-cards) with every
// cell stacked full-width. Here we lay the four middle stat cells out 2-per-row so the
// card is more compact. Scoped to this page so other DataView tables are unaffected.
// Column order: 1 Organisation · 2 Users · 3 Projects · 4 Can Publish · 5 Can Partner · 6 Last Activity
:deep(tbody.dt-grid-cards) tr {
  @include media-breakpoint-down(md) {
    flex-flow: row wrap;

    // Organisation (header) and Last Activity (footer) keep their own full-width row.
    td:first-child,
    td:last-child {
      flex: 0 0 100%;
    }

    // The org name is already the card's bold header — drop the redundant
    // "Organisation" label and let the name use the full width.
    td:first-child {
      grid-template-columns: 1fr;

      &::before {
        display: none;
      }
    }

    // Users / Projects / Can Publish / Can Partner — two per row.
    td:nth-child(2),
    td:nth-child(3),
    td:nth-child(4),
    td:nth-child(5) {
      flex: 0 0 50%;
    }
  }
}
</style>
